/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { GmcError, GmcStoreActionWithOptionalNotification } from '@app/core/types';
import { createAction, props } from '@ngrx/store';
import {
  ChangeTdeMasterKeyArgument,
  Cluster2xConnectionToken,
  ClusterCredentials,
  ClusterId,
  ClusterInfo,
  ToggleClusterFeature,
} from '@shared/types/cluster';
import { ClusterLicenseText } from '@shared/types/cluster-actions';
import { Cluster3xConnectionToken } from '@shared/types/cluster3x';
import { TopologySnapshot } from '@shared/types/node';
import { TeamId } from '@shared/types/team';

export const clusters = createAction('[Cluster] Clusters', props<{ data: ClusterInfo[] }>());
export const currentClusterChange = createAction('[Cluster] Current cluster change', props<{ id: string | null }>());
export const attachCluster = createAction(
  '[Cluster] Attach cluster',
  props<{
    connectionToken: Cluster2xConnectionToken | Cluster3xConnectionToken;
    clusterCredentials?: ClusterCredentials;
    teamId?: TeamId;
  }>(),
);
export const attachClusterOk = createAction('[Cluster] Attach cluster success', props<{ cluster: ClusterInfo }>());
export const attachClusterError = createAction('[Cluster] Failed to attach cluster', props<GmcError>());
export const clusterTopology = createAction(
  '[Cluster] Cluster topology',
  props<{ clusterId: string; data: TopologySnapshot }>(),
);

export const removeCluster = createAction('[Cluster] Remove cluster', props<{ clusterId: string }>());
export const removeClusterOk = createAction('[Cluster] Remove cluster success', props<{ notification: string }>());
export const removeClusterErr = createAction(
  '[Cluster] Remove cluster error',
  props<GmcError & { clusterId: string }>(),
);

export const confirmClusterActivation = createAction('[Cluster] Confirm Activate', props<{ clusterId: string }>());
export const activateCluster = createAction('[Cluster] Activate', props<{ clusterId: string }>());
export const activateClusterOk = createAction('[Cluster] Activate success', props<{ notification: string }>());
export const activateClusterErr = createAction('[Cluster] Activation error', props<GmcError & { clusterId: string }>());
export const confirmClusterDeactivation = createAction('[Cluster] Confirm Deactivate', props<{ clusterId: string }>());
export const deactivateCluster = createAction('[Cluster] Deactivate', props<{ clusterId: string }>());
export const deactivateClusterOk = createAction('[Cluster] Deactivate success', props<{ notification: string }>());
export const deactivateClusterErr = createAction(
  '[Cluster] Deactivation error',
  props<GmcError & { clusterId: string }>(),
);
export const detachClusterIntent = createAction('[Cluster] Confirm Detach', props<{ cluster: ClusterInfo }>());
export const detachCluster = createAction('[Cluster] Detach', props<{ cluster: ClusterInfo }>());
export const detachClusterOk = createAction('[Cluster] Detach success', props<{ notification: string }>());
export const detachClusterErr = createAction('[Cluster] Detach error', props<GmcError>());
export const getAgentVersionLinksError = createAction('[Cluster] Failed to get agent version links', props<GmcError>());
export const getClusterInfoError = createAction(
  '[Cluster] Failed to get a Cluster by connection token',
  props<GmcError>(),
);
export const changeTdeMasterKey = createAction(
  '[Cluster] Change TDE master key',
  props<{ clusterId: string; masterKey: ChangeTdeMasterKeyArgument }>(),
);
export const changeTdeMasterKeyOk = createAction(
  '[Cluster] Change TDE master key success',
  props<{ notification: string }>(),
);
export const changeTdeMasterKeyErr = createAction(
  '[Cluster] Change TDE master key error',
  props<GmcError & { clusterId: string }>(),
);

export const adminToggleClusterFeatures = createAction(
  '[Cluster] Admin toggle features',
  props<{ feature: ToggleClusterFeature; enabled: boolean; clusterId: ClusterId }>(),
);
export const adminToggleClusterFeaturesErr = createAction(
  '[Cluster] Admin toggle features err',
  props<{ feature: ToggleClusterFeature; enabled: boolean; clusterId: ClusterId } & GmcError>(),
);
export const adminToggleClusterFeaturesSucc = createAction(
  '[Cluster] Admin toggle features succ',
  props<{ notification: string }>(),
);

export const exportLogs = createAction('[Cluster] Export Logs', props<{ clusterId: string; date: Date }>());
export const exportLogsOk = createAction(
  '[Cluster] Export Logs OK',
  props<{ clusterId: string; resourceId: string; date: Date }>(),
);
export const exportLogsErr = createAction('[Cluster] Export Logs error', props<GmcError & { clusterId: string }>());
export const downloadLogs = createAction('[Cluster] Download Logs', props<{ resourceId: string; date: Date }>());

export const downloadLogsErr = createAction('[Cluster] Download Logs error', props<GmcError>());

// Update license
export const updateClusterLicenseAction = createAction(
  '[Cluster] Update cluster license',
  props<{ license: ClusterLicenseText; clusterId: ClusterId }>(),
);
export const updateClusterLicenseOk = createAction(
  '[Cluster] Update cluster license OK',
  props<{ clusterId: ClusterId } & GmcStoreActionWithOptionalNotification>(),
);
export const updateClusterLicenseErr = createAction(
  '[Cluster] Update cluster license err',
  props<GmcError & { clusterId: ClusterId }>(),
);
